import { OrderVendorsAPI } from '@business/api/order';
import { OrderVendorId, OrderVendorResponse } from '@business/entities/order';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class OrderVendorBloc extends BaseBloc {
  private readonly vendorsRepo = new Repository<OrderVendorResponse>({
    getItemId: vendor => vendor.id,
  });

  readonly vendors$ = this.vendorsRepo.items$;
  readonly selectVendor = this.vendorsRepo.selectItem;

  onReset() {
    this.vendorsRepo.reset();
  }

  fetchVendors = () => {
    return OrderVendorsAPI.fetchVendors().pipe(this.vendorsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_vendors_error') }));
  };

  fetchVendor = (id: OrderVendorId) => {
    return OrderVendorsAPI.fetchVendor(id).pipe(
      this.vendorsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_vendors_error') }),
    );
  };

  createVendor = (data: Partial<OrderVendorResponse>) => {
    return OrderVendorsAPI.createVendor(data).pipe(
      this.vendorsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_vendor'),
      }),
    );
  };

  updateVendor = (id: OrderVendorId, data: Partial<OrderVendorResponse>) => {
    return OrderVendorsAPI.updateVendor(id, data).pipe(
      this.vendorsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_vendor'),
      }),
    );
  };

  deleteVendor = (id: OrderVendorId) => {
    return showDeleteConfirmation('Delete Vendor', `Do you really want to remove this vendor?`).pipe(
      switchMap(() => OrderVendorsAPI.deleteVendor(id)),
      this.vendorsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_vendor'),
      }),
    );
  };

  sync = () => {
    return OrderVendorsAPI.sync().pipe(
      this.vendorsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_order_product'),
      }),
    );
  };
}
